import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { UserService } from "./user.service";
import { DateHelper } from "../core/helpers/date.helper";

/** localStorage key (set by the native app) -> request header read by ClientDeviceRequestReader (FLIP-579 / 583). */
const MOBILE_DEVICE_HEADERS: ReadonlyArray<readonly [string, string]> = [
  ['appVersion', 'X-App-Version'],
  ['appBuild', 'X-App-Build'],
  ['osVersion', 'X-OS-Version'],
  ['deviceClass', 'X-Device-Class'],
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(private userService: UserService, private router: Router) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // add authorization header with jwt token if available - falls back to the
    // pending (pre-login) token during registration so Stripe checkout/verify
    // calls can authenticate before the user is actually logged in
    const token = this.userService.getAuthToken();

    const headers: { [key: string]: string } = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add appPlatform header if running in mobile app (stored in localStorage)
    const appPlatform = localStorage.getItem('appPlatform');
    if (appPlatform === 'Android' || appPlatform === 'iOS') {
      headers['appPlatform'] = appPlatform;

      // App version, build, OS version and device class, injected into localStorage by the native
      // app next to appPlatform (FLIP-583). The WebView makes the registration and login calls itself,
      // so this is the only way the API (FLIP-579) can record them for those. An older app never sets
      // them and nothing is sent; the API validates the text and ignores anything it does not accept.
      for (const [storageKey, header] of MOBILE_DEVICE_HEADERS) {
        const value = localStorage.getItem(storageKey);
        if (value && value.length <= 50) {
          headers[header] = value;
        }
      }
    }

    // Installed PWA or normal browser tab, recorded with the device details at registration and
    // login (FLIP-579). The user-agent already says everything else about a browser; this is the one
    // thing it cannot. Same check as A2hsService.checkStandalone(). Skipped inside the native app's
    // WebView, which reports its own platform and is never a PWA.
    if (!headers['appPlatform']) {
      try {
        headers['X-App-Display-Mode'] = window.matchMedia('(display-mode: standalone)').matches
          ? 'standalone'
          : 'browser';
      } catch {
        // matchMedia unavailable: leave it out, the server stores "not reported".
      }
    }

    // The user's wall-clock time, stamped onto every user-authored row the request writes
    // (FLIP-218). Sent on every call rather than plumbed through individual request DTOs:
    // the server side reads it in ApplicationDbContext.SaveChangesAsync, so entities inherit
    // local-date capture instead of each table having to opt in.
    //
    // This is a capture-time reading, deliberately not derived later from the user's saved
    // timezone — a row written at 04:15 in Sydney would re-bucket to a different app day the
    // moment the user travels, and Nova reads band sequences, so that can manufacture a
    // transition that never happened.
    headers['X-Local-Time'] = DateHelper.formatToApiDateTime();
    headers['X-Tz-Offset'] = String(-new Date().getTimezoneOffset());

    if (Object.keys(headers).length > 0) {
      request = request.clone({
        setHeaders: headers,
      });
    }

    // nodebb_token is minted on login/register/NodeBBSession/CompleteOnboarding, re-minted on
    // ChangeUnconfirmedEmail (the new address is a claim inside it), and cleared on logout; the
    // browser only honours Set-Cookie on a cross-origin response when the request is
    // credentialed. Any endpoint that mints the cookie must be listed here or the browser
    // silently drops it.
    if (
      /\/api\/Account\/(Login|SelfRegister|Logout|NodeBBSession|CompleteOnboarding|ChangeUnconfirmedEmail)$/i.test(
        request.url
      )
    ) {
      request = request.clone({ withCredentials: true });
    }

    return next.handle(request).pipe(
      tap(
        () => {},
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            let failedCalls = [];

            if (localStorage.getItem("failedCalls")) {
              failedCalls = JSON.parse(localStorage.getItem("failedCalls"));
            }

            failedCalls.unshift({
              url: err.url,
              message: err.message,
            });

            localStorage.setItem("failedCalls", JSON.stringify(failedCalls));

            // let calls: string[] = [];
            // let deets: string[] = [];
            // var callsData = JSON.parse(localStorage.getItem('failedCalls'));
            // var deetsData = JSON.parse(localStorage.getItem('failureDeets'));
            // if (callsData !== null && deetsData !== null) {
            //   calls.push(JSON.parse(localStorage.getItem('failedCalls')));
            //   deets.push(JSON.parse(localStorage.getItem('failureDeets')));
            // }
            // calls.push(err.url);
            // deets.push(err.message);
            // localStorage.setItem('failedCalls', JSON.stringify(calls));
            // localStorage.setItem('failureDeets', JSON.stringify(deets));
            // Need to save these to localstorage as well.
            // NodeBBSession backs the community forum SSO/warmup call, not the user's actual
            // app session - a 401 there (e.g. NodeBB config/shared-secret issues) must not be
            // treated as "your login expired" and bounce the whole app to the login screen.
            const isCommunitySessionCall = /\/api\/Account\/NodeBBSession$/i.test(err.url ?? "");

            // A 401 only means "your login expired" for a request that CARRIED a login. One sent
            // with no token at all (a signed-out visitor, where something fires an authenticated call
            // at app start-up - NovaCompanionSettingsService does, from its constructor) says nothing
            // about a session, and bouncing it to /login made every public page unreachable by a
            // cold load: an emailed reset or confirm-email link opened in a fresh tab landed on the
            // login screen (FLIP-481). Routes that need a login are still protected by the route
            // guards, which do their own redirect.
            const sentCredentials = !!token;

            if (err.status === 401 && !isCommunitySessionCall && sentCredentials) {
              const wasOnlyPending =
                !this.userService.isUserLoggedIn() &&
                this.userService.hasPendingAuth();
              this.userService.logoutOnTokenExp();
              if (wasOnlyPending) {
                this.userService.clearPendingAuth();
                this.router.navigate(["register"]);
              } else {
                this.router.navigate(["login"]);
              }
            }

            if (err.status === 403) {
              this.redirectIfRegionUnavailable(err);
            }
          }
        }
      )
    );
  }

  /**
   * FLIP-553. The registration API answers 403 { error: "region_unavailable" } when the visitor is
   * outside the countries flip* is sold in; take them to the explanation page. The generated client
   * reads responses as blobs, so the error body can arrive as a Blob, a string or a parsed object.
   * Not an expired login, so it never goes through the 401 logout path above.
   */
  private redirectIfRegionUnavailable(err: HttpErrorResponse): void {
    const check = (body: any) => {
      if (body?.error === "region_unavailable") {
        this.router.navigate(["region-unavailable"]);
      }
    };
    const parse = (text: string) => {
      try {
        check(JSON.parse(text));
      } catch {
        // Not our JSON body: some other 403.
      }
    };

    const body = err.error;
    if (typeof Blob !== "undefined" && body instanceof Blob) {
      body.text().then(parse, () => {});
    } else if (typeof body === "string") {
      parse(body);
    } else {
      check(body);
    }
  }
}
